'use client';
 
import { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
 
// ============================================================================
// TYPES
// ============================================================================
 
interface ThemeConfiguration {
  themeName?: string;
  typography?: {
    fontFamily?: string;
  };
}
 
interface ThemeSwitcherProps {
  id?: string;
  themeConfiguration?: ThemeConfiguration;
}
interface Theme {
  name: string;
  value: string;
  color: string;
}
 
interface FontOption {
  key: string;
  name: string;
  value: string;
  googleFont: string | null;
  category: string;
}
 
// ============================================================================
// CONSTANTS
// ============================================================================
 
const PREVIEW_PARAM = 'preview';
const THEME_PARAM = 'theme';
const FONT_PARAM = 'font';
const THEME_STORAGE_KEY = 'theme';
const FONT_STORAGE_KEY = 'font';
 
const themes: Theme[] = [
  { name: 'Forest', value: 'forest', color: 'bg-[#2f6fe4]' },
  { name: 'Blue', value: 'blue', color: 'bg-[#2f6fe4]' },
  { name: 'Olive', value: 'olive', color: 'bg-[#4f5d2f]' },
  { name: 'Peach', value: 'peach', color: 'bg-[#e85c3a]' },
  { name: 'Lavender', value: 'lavender', color: 'bg-[#7b3fc7]' },
  { name: 'Mint', value: 'mint', color: 'bg-[#2e9e7f]' },
  { name: 'Champagne', value: 'champagne', color: 'bg-[#caa45c]' },
  { name: 'Terracotta', value: 'terracotta', color: 'bg-[#c75c3a]' },
  { name: 'Ocean', value: 'ocean', color: 'bg-[#0b84a5]' },
  { name: 'Midnight', value: 'midnight', color: 'bg-[#5865f2]' },
  { name: 'Rose', value: 'rose', color: 'bg-[#d6336c]' },
  { name: 'Desert', value: 'desert', color: 'bg-[#a67c52]' },
  { name: 'Neon', value: 'neon', color: 'bg-[#00f2ff]' },
];
 
const fontOptions: FontOption[] = [
  {
    key: 'inter',
    name: 'Inter',
    value: '"Inter", sans-serif',
    googleFont: 'Inter:wght@400;500;600;700',
    category: 'sans-serif'
  },
  {
    key: 'poppins',
    name: 'Poppins',
    value: '"Poppins", sans-serif',
    googleFont: 'Poppins:wght@300;400;500;600',
    category: 'sans-serif'
  },
  {
    key: 'roboto',
    name: 'Roboto',
    value: '"Roboto", sans-serif',
    googleFont: 'Roboto:wght@300;400;500;700',
    category: 'sans-serif'
  },
  {
    key: 'montserrat',
    name: 'Montserrat',
    value: '"Montserrat", sans-serif',
    googleFont: 'Montserrat:wght@400;500;600;700',
    category: 'sans-serif'
  },
  {
    key: 'open-sans',
    name: 'Open Sans',
    value: '"Open Sans", sans-serif',
    googleFont: 'Open+Sans:wght@300;400;500;600',
    category: 'sans-serif'
  },
  {
    key: 'playfair',
    name: 'Playfair Display',
    value: '"Playfair Display", serif',
    googleFont: 'Playfair+Display:wght@400;500;600;700',
    category: 'serif'
  },
  {
    key: 'merriweather',
    name: 'Merriweather',
    value: '"Merriweather", serif',
    googleFont: 'Merriweather:wght@300;400;700',
    category: 'serif'
  },
  {
    key: 'lora',
    name: 'Lora',
    value: '"Lora", serif',
    googleFont: 'Lora:wght@400;500;600;700',
    category: 'serif'
  },
  {
    key: 'crimson',
    name: 'Crimson Text',
    value: '"Crimson Text", serif',
    googleFont: 'Crimson+Text:wght@400;600;700',
    category: 'serif'
  },
  {
    key: 'lexend',
    name: 'Lexend',
    value: '"Lexend", sans-serif',
    googleFont: 'Lexend:wght@300;400;500;600',
    category: 'sans-serif'
  },
  {
    key: 'outfit',
    name: 'Outfit',
    value: '"Outfit", sans-serif',
    googleFont: 'Outfit:wght@200;300;400;500',
    category: 'sans-serif'
  },
  {
    key: 'plus-jakarta',
    name: 'Plus Jakarta',
    value: '"Plus Jakarta Sans", sans-serif',
    googleFont: 'Plus+Jakarta+Sans:wght@400;500;600;700',
    category: 'sans-serif'
  },
  {
    key: 'fira-code',
    name: 'Fira Code',
    value: '"Fira Code", monospace',
    googleFont: 'Fira+Code:wght@300;400;500;600',
    category: 'monospace'
  },
  {
    key: 'jetbrains',
    name: 'JetBrains Mono',
    value: '"JetBrains Mono", monospace',
    googleFont: 'JetBrains+Mono:wght@300;400;500;600',
    category: 'monospace'
  },
  {
    key: 'space-mono',
    name: 'Space Mono',
    value: '"Space Mono", monospace',
    googleFont: 'Space+Mono:wght@400;700',
    category: 'monospace'
  },
  {
    key: 'system-sans',
    name: 'System Sans',
    value: 'system-ui, -apple-system, sans-serif',
    googleFont: null,
    category: 'system'
  },
  {
    key: 'system-serif',
    name: 'System Serif',
    value: 'ui-serif, Georgia, serif',
    googleFont: null,
    category: 'system'
  },
];
 
const THEME_NAME_MAP: Record<string, string> = {
  forest: 'forest',
  blue: 'blue',
  olive: 'olive',
  peach: 'peach',
  lavender: 'lavender',
  mint: 'mint',
  champagne: 'champagne',
  terracotta: 'terracotta',
  ocean: 'ocean',
  midnight: 'midnight',
  rose: 'rose',
  desert: 'desert',
  neon: 'neon',
  sunset: 'sunset',
  dark: "dark",
};
 
// ============================================================================
// UTILITIES
// ============================================================================
 
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Silent fail for privacy mode
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Silent fail
    }
  },
};
 
const normalizeFontFamily = (fontFamily: string): string => {
  return fontFamily
    .split(',')[0]
    .replace(/["']/g, '')
    .trim()
    .toLowerCase();
};
 
const extractFontName = (fontValue: string): string => {
  return fontValue.split(',')[0].replace(/["']/g, '').trim();
};
 
// ============================================================================
// COMPONENT
// ============================================================================
 
export default function ThemeSwitcher({ id, themeConfiguration }: ThemeSwitcherProps) {
 
  const router = useRouter();
  const searchParams = useSearchParams();
 
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'colors' | 'fonts'>('colors');
  const [currentTheme, setCurrentTheme] = useState<string>('default');
  const [currentFont, setCurrentFont] = useState<string>('system-sans');
  const [isFontLoading, setIsFontLoading] = useState(false);
 
  const loadedFontsRef = useRef<Set<string>>(new Set());
  const panelRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
 
  const previewMode = searchParams.get(PREVIEW_PARAM) === 'true';
 
  // Backend defaults
  const backendTheme = THEME_NAME_MAP[themeConfiguration?.themeName?.toLowerCase() || 'forest'] || 'default';
  const backendFont = themeConfiguration?.typography?.fontFamily || 'system-ui, -apple-system, sans-serif';
 
  // ============================================================================
  // FONT LOADING
  // ============================================================================
 
  // Update your loadGoogleFont function in ThemeSwitcher
  const loadGoogleFont = useCallback((fontOption: FontOption): Promise<void> => {
    return new Promise((resolve) => {
      if (!fontOption.googleFont) {
        resolve();
        return;
      }
 
      if (loadedFontsRef.current.has(fontOption.key)) {
        resolve();
        return;
      }
 
      const existingLink = document.querySelector(`link[data-font="${fontOption.key}"]`);
      if (existingLink) {
        loadedFontsRef.current.add(fontOption.key);
        resolve();
        return;
      }
 
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${fontOption.googleFont}&display=swap`;
      link.setAttribute('data-font', fontOption.key);
 
      // Add media="print" to prevent render blocking, then switch to "all" on load
      link.media = 'print';
 
      link.onload = () => {
        link.media = 'all';
        loadedFontsRef.current.add(fontOption.key);
        resolve();
      };
 
      link.onerror = () => {
        console.warn(`Failed to load font: ${fontOption.name}`);
        resolve(); // Don't reject, just continue
      };
 
      document.head.appendChild(link);
    });
  }, []);
 
  const applyFont = useCallback(async (fontOption: FontOption) => {
    setIsFontLoading(true);
 
    try {
      // Load Google Font if needed
      if (fontOption.googleFont) {
        await loadGoogleFont(fontOption);
 
        // Wait for font to be ready
        const fontName = extractFontName(fontOption.value);
        try {
          await document.fonts.load(`1em ${fontName}`);
          // Small delay to ensure font is fully rendered
          await new Promise(resolve => setTimeout(resolve, 50));
        } catch (err) {
          console.warn(`Font loading timeout for ${fontName}, proceeding anyway`);
        }
      }
 
      // Apply CSS variable
      document.documentElement.style.setProperty('--app-font-family', fontOption.value);
      setCurrentFont(fontOption.key);
    } catch (err) {
      console.error('Error applying font:', err);
      // Fallback to system font on error
      const systemFont = fontOptions.find(f => f.key === 'system-sans')!;
      document.documentElement.style.setProperty('--app-font-family', systemFont.value);
      setCurrentFont(systemFont.key);
    } finally {
      setIsFontLoading(false);
    }
  }, [loadGoogleFont]);
 
  // ============================================================================
  // THEME APPLICATION
  // ============================================================================
 
  const applyTheme = useCallback((themeValue: string) => {
    const mappedTheme = THEME_NAME_MAP[themeValue] || 'default';
    document.documentElement.setAttribute('data-theme', mappedTheme);
    setCurrentTheme(themeValue);
  }, []);
 
  // ============================================================================
  // INITIAL SETUP
  // ============================================================================
 
  useEffect(() => {
    const queryTheme = searchParams.get(THEME_PARAM);
    const queryFont = searchParams.get(FONT_PARAM);
 
    let themeToApply = backendTheme;
    let fontToApply: FontOption | null = null;
 
    if (previewMode) {
      // Priority: URL params > localStorage > backend defaults
      themeToApply = queryTheme || safeLocalStorage.getItem(THEME_STORAGE_KEY) || backendTheme;
 
      if (queryFont) {
        fontToApply = fontOptions.find(f => f.key === queryFont) || null;
      } else {
        const storedFont = safeLocalStorage.getItem(FONT_STORAGE_KEY);
        if (storedFont) {
          fontToApply = fontOptions.find(f => f.key === storedFont) || null;
        }
      }
    }
 
    // Apply theme
    applyTheme(themeToApply);
 
    // Find font to apply
    if (!fontToApply) {
      // Try to match backend font
      const normalizedBackendFont = normalizeFontFamily(backendFont);
      fontToApply = fontOptions.find(f =>
        normalizeFontFamily(f.value) === normalizedBackendFont
      ) || fontOptions.find(f => f.key === 'system-sans')!;
    }
 
    // Apply font
    applyFont(fontToApply);
  }, [searchParams, previewMode, backendTheme, backendFont, applyTheme, applyFont]);
 
  // ============================================================================
  // UPDATE HANDLERS
  // ============================================================================
 
  const updateTheme = useCallback((theme: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set(PREVIEW_PARAM, 'true');
    params.set(THEME_PARAM, theme);
 
    // If switching to forest, reset font to backend default
    if (theme === 'forest') {
      params.delete(FONT_PARAM);
      safeLocalStorage.removeItem(FONT_STORAGE_KEY);
 
      // Find and apply backend font
      const normalizedBackendFont = normalizeFontFamily(backendFont);
      const backendFontOption = fontOptions.find(f =>
        normalizeFontFamily(f.value) === normalizedBackendFont
      ) || fontOptions.find(f => f.key === 'system-sans')!;
 
      applyFont(backendFontOption);
    }
 
    safeLocalStorage.setItem(THEME_STORAGE_KEY, theme);
    applyTheme(theme);
    router.replace(`?${params.toString()}`, { scroll: false });
  }, [searchParams, router, applyTheme, applyFont, backendFont]);
 
  const updateFont = useCallback((fontKey: string) => {
    const fontOption = fontOptions.find(f => f.key === fontKey);
    if (!fontOption) return;
 
    const params = new URLSearchParams(searchParams.toString());
    params.set(PREVIEW_PARAM, 'true');
    params.set(FONT_PARAM, fontKey);
 
    safeLocalStorage.setItem(FONT_STORAGE_KEY, fontKey);
    applyFont(fontOption);
    router.replace(`?${params.toString()}`, { scroll: false });
  }, [searchParams, router, applyFont]);
 
  const resetToDefaults = useCallback(() => {
    safeLocalStorage.removeItem(THEME_STORAGE_KEY);
    safeLocalStorage.removeItem(FONT_STORAGE_KEY);
 
    applyTheme(backendTheme);
 
    const normalizedBackendFont = normalizeFontFamily(backendFont);
    const backendFontOption = fontOptions.find(f =>
      normalizeFontFamily(f.value) === normalizedBackendFont
    ) || fontOptions.find(f => f.key === 'system-sans')!;
 
    applyFont(backendFontOption);
 
    router.replace(`?${PREVIEW_PARAM}=true`, { scroll: false });
  }, [backendTheme, backendFont, applyTheme, applyFont, router]);
 
  // ============================================================================
  // KEYBOARD & ACCESSIBILITY
  // ============================================================================
 
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        toggleButtonRef.current?.focus();
      }
    };
 
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isOpen &&
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        toggleButtonRef.current &&
        !toggleButtonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
 
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
 
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);
 
  // Focus trap
  useEffect(() => {
    if (!isOpen || !panelRef.current) return;
 
    const focusableElements = panelRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
 
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
 
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
 
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };
 
    panelRef.current.addEventListener('keydown', handleTab);
 
    return () => {
      panelRef.current?.removeEventListener('keydown', handleTab);
    };
  }, [isOpen]);
 
  // ============================================================================
  // RENDER
  // ============================================================================
 
  if (!previewMode) return null;
 
  return (
    <div className="fixed right-4 top-20 z-50">
      <button
        ref={toggleButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-label={isOpen ? "Close theme settings" : "Open theme settings"}
        aria-expanded={isOpen}
      >
        <svg
          className="w-6 h-6 text-gray-700"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
          />
        </svg>
      </button>
 
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={panelRef}
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 300, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="absolute right-20 top-0 max-h-screen w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Theme and font customization"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900">Customize</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Close settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
 
            {/* Tabs */}
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setActiveTab('colors')}
                className={`flex-1 py-3 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${activeTab === 'colors'
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                role="tab"
                aria-selected={activeTab === 'colors'}
                aria-controls="colors-panel"
              >
                Colors
              </button>
              <button
                onClick={() => setActiveTab('fonts')}
                className={`flex-1 py-3 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${activeTab === 'fonts'
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                role="tab"
                aria-selected={activeTab === 'fonts'}
                aria-controls="fonts-panel"
              >
                Fonts
              </button>
            </div>
 
            {/* Content */}
            <div className="p-4 max-h-[300px] overflow-y-auto">
              {activeTab === 'colors' ? (
                <div
                  id="colors-panel"
                  role="tabpanel"
                  aria-labelledby="colors-tab"
                  className="grid grid-cols-3 gap-3"
                >
                  {themes.map((theme) => {
                    const isActive = currentTheme === theme.value;
                    return (
                      <button
                        key={theme.value}
                        onClick={() => updateTheme(theme.value)}
                        className="flex flex-col items-center group focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-lg p-2 transition-transform hover:scale-105"
                        aria-label={`${theme.name} theme`}
                        aria-pressed={isActive}
                      >
                        <div
                          className={`w-14 h-14 rounded-full ${theme.color} shadow-md border-4 transition-all ${isActive
                            ? 'border-blue-500 ring-4 ring-blue-200'
                            : 'border-white group-hover:border-gray-200'
                            }`}
                        />
                        <span className={`mt-2 text-xs font-medium truncate w-full text-center transition-colors ${isActive ? 'text-blue-600' : 'text-gray-700 group-hover:text-gray-900'
                          }`}>
                          {theme.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div
                  id="fonts-panel"
                  role="tabpanel"
                  aria-labelledby="fonts-tab"
                  className="space-y-2"
                >
                  {fontOptions.map((font) => {
                    const isActive = currentFont === font.key;
                    const isLoading = isFontLoading && isActive;
 
                    return (
                      <button
                        key={font.key}
                        onClick={() => updateFont(font.key)}
                        disabled={isFontLoading}
                        className={`w-full p-3 text-left rounded-lg border transition-all flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${isActive
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                          }`}
                        style={{ fontFamily: font.value }}
                        aria-pressed={isActive}
                      >
                        <div className="flex gap-5">
                          <span className={`text-sm font-medium ${isActive ? 'text-blue-700' : 'text-gray-900'
                            }`}>
                            {font.name}:
                          </span>
                          <span className="text-xs text-gray-500 mt-0.5">
                            {font.category}
                          </span>
                        </div>
                        {isLoading && (
                          <span className="text-blue-600 text-xs animate-pulse">Loading...</span>
                        )}
                        {isActive && !isLoading && (
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
 
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs text-blue-900 font-medium">
                      💡 Tip
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      System fonts load instantly and use less bandwidth. Google fonts offer more design variety.
                    </p>
                  </div>
                </div>
              )}
            </div>
 
            {/* Footer */}
            <div className="border-t border-gray-200 p-3 bg-gray-50">
              <button
                onClick={resetToDefaults}
                disabled={isFontLoading}
                className="w-full py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset to Defaults
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
 